import CourtPhoto from '@/components/racquet/CourtPhoto'
import RacquetContinueSessions from './RacquetContinueSessions'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import LetsPlayLoading from './LetsPlayLoading'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { racquetPath, racquetResponse } from '../lib/racquetApi'
import './racquetPlay.css'
const uuid = () => crypto.randomUUID()
export default function LetsPlayRacquetSetup({
  clubCode,
  sport,
  onBackToSports
}) {
  const { locale, t: translate } = useOptionalPrivanaI18n(),
    t = (k) => translate(`racquetPlay.${k}`)
  const { memberPortalApiGet: get, memberPortalApiPost: post } =
      useMemberPortalApi(),
    navigate = useNavigate(),
    [query] = useSearchParams()
  const [data, setData] = useState(null),
    [matches, setMatches] = useState([]),
    [error, setError] = useState(''),
    [step, setStep] = useState(0),
    [busy, setBusy] = useState(false)
  const [courtId, setCourt] = useState(''),
    [format, setFormat] = useState(1),
    [players, setPlayers] = useState([]),
    [teams] = useState(() => [uuid(), uuid()])
  const [search, setSearch] = useState(''),
    [results, setResults] = useState([]),
    [searched, setSearched] = useState(false),
    [guest, setGuest] = useState(''),
    attempt = useRef(null),
    lock = useRef(false)
  const load = useCallback(async () => {
    setError('')
    try {
      const [d, m] = await Promise.all([
        get(
          racquetPath(
            clubCode,
            `/setup?sport=${sport}${query.get('bookingId') ? `&bookingId=${encodeURIComponent(query.get('bookingId'))}` : ''}`
          )
        ).then(racquetResponse),
        get(
          racquetPath(clubCode, `/matches?sport=${sport}&status=resumable`)
        ).then(racquetResponse)
      ])
      setData(d)
      setMatches(m)
      setPlayers([
        {
          id: uuid(),
          type: 'member',
          memberId: d.me.id,
          displayName: d.me.display_name,
          teamId: teams[0]
        }
      ])
      if (d.prefill) {
        setCourt(d.prefill.courtId)
        const c = d.courts.find((c) => c.id === d.prefill.courtId)
        setFormat(c?.formats[0] || 1)
        const capacity = (c?.formats[0] || 1) * 2
        setPlayers([
          {
            id: uuid(),
            type: 'member',
            memberId: d.me.id,
            displayName: d.me.display_name,
            teamId: teams[0]
          },
          ...d.prefill.players
            .filter((p) => p.id !== d.me.id)
            .slice(0, capacity - 1)
            .map((p, i) => ({
              id: uuid(),
              type: 'member',
              memberId: p.id,
              displayName: p.display_name,
              teamId: teams[(i + 1) % 2]
            }))
        ])
      }
    } catch {
      setError('error')
    }
  }, [get, clubCode, sport])
  useEffect(() => {
    load()
  }, [load])
  const court = data?.courts.find((c) => c.id === courtId)
  const stages = court?.formats.length === 1 ? [0, 2, 3] : [0, 1, 2, 3]
  const previousStep = () =>
    setStep(stages[Math.max(0, stages.indexOf(step) - 1)])
  useEffect(() => {
    if (search.trim().length < 2) {
      setResults([])
      setSearched(false)
      return
    }
    let alive = true
    const timer = setTimeout(async () => {
      try {
        const rows = await get(
          `/api/members-portal/${clubCode}/members/search?q=${encodeURIComponent(search.trim())}`
        ).then(racquetResponse)
        if (alive) {
          setResults(rows)
          setSearched(true)
        }
      } catch {
        if (alive) setError('error')
      }
    }, 250)
    return () => {
      alive = false
      clearTimeout(timer)
    }
  }, [search, get, clubCode])
  const add = (person) => {
    if (
      players.length >= format * 2 ||
      players.some((p) => p.memberId === person.id)
    )
      return
    setPlayers((old) => [
      ...old,
      {
        id: uuid(),
        type: 'member',
        memberId: person.id,
        displayName:
          person.display_name ||
          `${person.first_name || ''} ${person.last_name || ''}`.trim(),
        teamId:
          old.filter((p) => p.teamId === teams[0]).length < format
            ? teams[0]
            : teams[1]
      }
    ])
    setSearch('')
  }
  const isValid =
    players.length === format * 2 &&
    teams.every(
      (id) => players.filter((p) => p.teamId === id).length === format
    )
  const start = async () => {
    if (lock.current) return
    lock.current = true
    setBusy(true)
    setError('')
    try {
      if (!attempt.current)
        attempt.current = {
          id: uuid(),
          sport,
          courtId,
          sessionMode: 'casual',
          participants: teams
            .flatMap((teamId) => players.filter((p) => p.teamId === teamId))
            .map(({ displayName, ...p }) => p)
        }
      const match = await post(
        racquetPath(clubCode, '/matches'),
        attempt.current
      ).then(racquetResponse)
      navigate(`/${clubCode}/lets-play/${sport}/match/${match.id}`)
    } catch (error) {
      if ([400, 403, 404, 422].includes(error.status)) attempt.current = null
      setError('error')
      setBusy(false)
      lock.current = false
    }
  }
  if (!data)
    return (
      <div className="rp">
        {error ? (
          <p role="alert">{t(error)}</p>
        ) : (
          <LetsPlayLoading
            label={t('preparingMatch')}
            detail={t('preparingDetail')}
          />
        )}
        {error && <button onClick={load}>{t('retry')}</button>}
      </div>
    )
  return (
    <div className="rp rp-setup">
      <button
        className="mp-golf-setup__back"
        onClick={() => (step ? previousStep() : onBackToSports())}
        disabled={busy}
      >
        <ArrowLeft size={17} /> {t(step === 0 ? 'sports' : ['court', 'format', 'players'][stages[stages.indexOf(step) - 1]])}
      </button>
      <div
        className="mp-golf-setup__progress"
        role="progressbar"
        aria-label={t('setupProgress')}
        aria-valuemin={0}
        aria-valuemax={stages.length}
        aria-valuenow={stages.indexOf(step) + 1}
      >
        <span
          style={{
            width: `${((stages.indexOf(step) + 1) / stages.length) * 100}%`
          }}
        />
      </div>
      {error && (
        <p role="alert" className="rp-notice">
          {error}
        </p>
      )}
      <fieldset
        disabled={busy || Boolean(attempt.current)}
        className="rp-fields"
      >
        <p className="mp-eyebrow">{t(sport)}</p>
        {step === 0 && (
          <>
            {matches.length > 0 && (
              <RacquetContinueSessions clubCode={clubCode} sport={sport} matches={matches} t={t} locale={locale} post={post} navigate={navigate} onRemoved={id => setMatches(old => old.filter(m => m.id !== id))} />
            )}
            <h1 id="lets-play-title">{t('where')}</h1>
            <div className="rp-courts">
              {data.courts.map((c) => (
                <button
                  className={`rp-court mp-golf-course-choice ${courtId === c.id ? 'is-selected' : ''}`}
                  key={c.id}
                  onClick={() => {
                    setCourt(c.id)
                    if (!c.formats.includes(format)) {
                      setFormat(c.formats[0])
                      setPlayers((p) => p.slice(0, 1))
                    }
                    if (!c.non_members_allowed)
                      setPlayers((ps) => ps.filter((p) => p.type === 'member'))
                    setStep(c.formats.length === 1 ? 2 : 1)
                  }}
                >
                  <RacquetCourtImage url={c.image_url} fallback={c.facility_image_url} />
                  <span className="rp-court-copy">
                    <strong>{c.name}</strong>
                    <small>
                      {[
                        c.is_outdoor == null
                          ? null
                          : t(c.is_outdoor ? 'outdoor' : 'indoor'),
                        ['single', 'double'].includes(c.court_size) ? t(`courtSize.${c.court_size}`) : null,
                        c.surface
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  </span>
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              ))}
            </div>
            {!data.courts.length && (
              <p className="rp-notice">{t('noCourts')}</p>
            )}
          </>
        )}
        {step === 1 && (
          <>
            <h1 id="lets-play-title">{t('how')}</h1>
            <div className="rp-options">
              {court?.formats.map((n) => (
                <button
                  key={n}
                  aria-pressed={format === n}
                  onClick={() => {
                    setFormat(n)
                    if (n !== format) setPlayers((p) => p.slice(0, 1))
                  }}
                >
                  {t(n === 1 ? 'singles' : 'doubles')}
                </button>
              ))}
            </div>
            <button
              className="rp-primary"
              disabled={!court}
              onClick={() => setStep(2)}
            >
              {t('next')}
            </button>
          </>
        )}
        {step === 2 && (
          <>
            <h1 id="lets-play-title">{t('who')}</h1>
            <div className="rp-team-grid">
              {teams.map((id, i) => (
                <section className="rp-team" key={id}>
                  <h2>
                    {t('team')} {i + 1}
                  </h2>
                  {players
                    .filter((p) => p.teamId === id)
                    .map((p) => (
                      <div key={p.id}>
                        <strong>{p.displayName}</strong>
                        <small>
                          {t(p.type === 'guest' ? 'guest' : 'member')}
                        </small>
                        <div className="rp-inline">
                          <button
                            aria-label={`${t('move')} ${p.displayName}`}
                            onClick={() =>
                              setPlayers((ps) =>
                                ps.map((x) =>
                                  x.id === p.id
                                    ? { ...x, teamId: teams[1 - i] }
                                    : x
                                )
                              )
                            }
                          >
                            {t('move')}
                          </button>
                          {p.memberId !== data.me.id && (
                            <button
                              aria-label={`${t('remove')} ${p.displayName}`}
                              onClick={() =>
                                setPlayers((ps) =>
                                  ps.filter((x) => x.id !== p.id)
                                )
                              }
                            >
                              {t('remove')}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                </section>
              ))}
            </div>
            {players.length < format * 2 && (
              <>
                <label>
                  {t('search')}
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </label>
                {searched && !results.length && <p>{t('noResults')}</p>}
                <div className="rp-options">
                  {results.map((p) => (
                    <button
                      key={p.id}
                      disabled={players.some((x) => x.memberId === p.id)}
                      onClick={() => add(p)}
                    >
                      {p.display_name || `${p.first_name} ${p.last_name}`}
                    </button>
                  ))}
                </div>
                <h3>{t('recent')}</h3>
                <div className="rp-options">
                  {data.recentPlayers.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => add(p)}
                      disabled={players.some((x) => x.memberId === p.id)}
                    >
                      {p.display_name}
                    </button>
                  ))}
                </div>
                {court?.non_members_allowed && (
                  <label>
                    {t('guest')}
                    <div className="rp-inline">
                      <input
                        maxLength={100}
                        value={guest}
                        onChange={(e) => setGuest(e.target.value)}
                      />
                      <button
                        disabled={
                          !guest.trim() ||
                          players.some(
                            (p) =>
                              p.displayName.toLocaleLowerCase() ===
                              guest.trim().toLocaleLowerCase()
                          )
                        }
                        onClick={() => {
                          setPlayers((ps) => [
                            ...ps,
                            {
                              id: uuid(),
                              type: 'guest',
                              displayName: guest.trim(),
                              teamId:
                                ps.filter((p) => p.teamId === teams[0]).length <
                                format
                                  ? teams[0]
                                  : teams[1]
                            }
                          ])
                          setGuest('')
                        }}
                      >
                        {t('addGuest')}
                      </button>
                    </div>
                  </label>
                )}
              </>
            )}
            {!isValid && <p>{t('requiredPlayers')}</p>}
            <button
              className="rp-primary"
              disabled={!isValid}
              onClick={() => setStep(3)}
            >
              {t('review')}
            </button>
          </>
        )}
        {step === 3 && (
          <>
            <h1 id="lets-play-title">{t('ready')}</h1>
            {[
              ['court', court?.name, 0],
              ...(court?.formats.length > 1
                ? [['match', t(format === 1 ? 'singles' : 'doubles'), 1]]
                : []),
              [
                'players',
                teams
                  .map((id) =>
                    players
                      .filter((p) => p.teamId === id)
                      .map((p) => p.displayName)
                      .join(' & ')
                  )
                  .join(' · '),
                2
              ]
            ].map(([key, value, index]) => (
              <section className="rp-review" key={key}>
                <div>
                  <h2>{t(key)}</h2>
                  <button onClick={() => setStep(index)}>{t('edit')}</button>
                </div>
                <p>{value}</p>
              </section>
            ))}
          </>
        )}
      </fieldset>
      {step === 3 && (
        <button
          className="rp-primary"
          onClick={start}
          disabled={busy || !isValid}
        >
          {t(busy ? 'starting' : 'start')}
        </button>
      )}
    </div>
  )
}

function RacquetCourtImage({ url, fallback }) {
  return <CourtPhoto imageUrl={url} fallbackUrl={fallback} className="rp-court-art" />
}
